import { useEffect, useMemo, useRef } from 'react';
import {
  FiArrowLeft, FiArchive, FiPlus, FiTrash2, FiFileText, FiCreditCard, FiCalendar, FiLock,
} from 'react-icons/fi';
import { ACTIONS, FORM, EMPTY } from '../../constants/labels';
import { fmtCurrency } from '../../utils/formatNumber';
import { formatDate } from '../../utils/dateUtils';
import { OFFERING_LABEL, newItem } from './offeringsConfig';
import { isFutureFinancialYear } from '../../utils/financialYears';
import OfferingsMemberSelect from './OfferingsMemberSelect';
import OfferingsCategorySelect from './OfferingsCategorySelect';
import rStyles from '../Receipts/NewReceiptsPage.module.css';
import styles from './OfferingsPage.module.css';

/** Prevent mouse-wheel scroll from changing a focused number input's value */
const blockWheel = (e) => e.target.blur();

export default function OfferingsForm({
  form, setForm,
  years, categories, events = [], eventDonations = [], personUsers = [],
  hideSpecialCategories = false,
  isDerasarAdmin, saving,
  isOnlinePaymentEnabled = false,
  editMode = false,
  isDateLocked = false,
  financialYearOptions = null,
  lockFinancialYearSelection = false,
  syncActiveFinancialYear = true,
  minDate = '',
  maxDate = '',
  onClose, onSubmit, onSubmitWithReceipt,
  onUserSelect,
  onRegisterSpecialDonation,
}) {
  const qtyRefs = useRef({});
  const rateRefs = useRef({});
  const amountRefs = useRef({});
  const sortedCategories = useMemo(() => {
    let cats = categories ? [...categories] : [];
    if (eventDonations.length > 0) {
      const visibleDonations = hideSpecialCategories
        ? eventDonations.filter((ed) => !ed.isSpecial)
        : eventDonations;
      const allowedIds = new Set(visibleDonations.map((ed) => String(ed.donationCategoryId)));
      cats = cats.filter((c) => allowedIds.has(String(c.id)));
    }
    return cats.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [categories, eventDonations, hideSpecialCategories]);

  const edByCategory = useMemo(() => {
    const map = new Map();
    eventDonations.forEach((ed) => map.set(String(ed.donationCategoryId), ed));
    return map;
  }, [eventDonations]);

  const categoryOptions = useMemo(() => sortedCategories.map((c) => {
    const ed = edByCategory.get(String(c.id));
    return {
      ...c,
      disabled: ed?.isFull === true,
      badge: ed?.isFull ? `Full (${ed.currentCount}/${ed.maxCount})` : ed?.isSpecial ? 'Blind Offer' : null,
    };
  }), [sortedCategories, edByCategory]);
  /* ── item helpers ─────────────────────────────────────────────────────── */
  const updateItem = (id, patch) =>
    setForm((f) => ({ ...f, items: f.items.map((it) => it.id === id ? { ...it, ...patch } : it) }));

  const focusItemInput = (refs, id) => {
    setTimeout(() => refs.current[id]?.focus(), 0);
  };

  const removeItem = (id) =>
    setForm((f) => ({ ...f, items: f.items.filter((it) => it.id !== id) }));

  const addItem = () =>
    setForm((f) => ({ ...f, items: [...f.items, newItem()] }));

  const itemAmount = (it) => parseFloat(it.amount) || 0;
  const totalAmount = form.items.reduce((s, it) => s + itemAmount(it), 0);

  const validItemCount = form.items.filter(
    (it) => !it.isSpecialDonation && it.donationCategoryId && parseFloat(it.amount) > 0
  ).length;
  const matchingEvents = useMemo(() => {
    if (!form.offeringDate) return [];

    return events.filter((event) => {
      const start = (event.eventDate || '').slice(0, 10);
      const end = (event.endDate || event.eventDate || '').slice(0, 10);
      return start && end && form.offeringDate >= start && form.offeringDate <= end;
    });
  }, [events, form.offeringDate]);

  /* ── FY constraints ───────────────────────────────────────────────────── */
  const selectedFY = years.find((y) => String(y.id) === String(form.financialYearId));
  const fyStart = selectedFY?.startDate?.slice(0, 10);
  const fyEnd = selectedFY?.endDate?.slice(0, 10);

  useEffect(() => {
    if (!selectedFY || !form.offeringDate) return;

    let nextDate = form.offeringDate;
    const lowerBound = minDate || fyStart;
    const upperBound = maxDate || fyEnd;

    if (lowerBound && nextDate < lowerBound) nextDate = lowerBound;
    if (upperBound && nextDate > upperBound) nextDate = upperBound;

    if (nextDate !== form.offeringDate) {
      setForm((current) => ({ ...current, offeringDate: nextDate }));
    }
  }, [form.offeringDate, fyEnd, fyStart, minDate, maxDate, selectedFY, setForm]);

  /* ── submit label ─────────────────────────────────────────────────────── */
  const submitLabel = saving
    ? (editMode ? 'Saving…' : ACTIONS.creating)
    : editMode
      ? 'Save Changes'
      : validItemCount > 1
        ? `Create ${validItemCount} ${OFFERING_LABEL.plural}`
        : ACTIONS.create(OFFERING_LABEL);

  const withReceiptLabel = saving 
    ? 'Saving…' 
    : isDerasarAdmin 
      ? '+ Create Receipt' 
      : '+ Pay Offers';
  const canPayAfterCreate = isDerasarAdmin
    ? true
    : isOnlinePaymentEnabled && (!selectedFY || !isFutureFinancialYear(selectedFY));

  return (
    <div className={styles.formPage}>
      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div className="page-header" style={{ marginBottom: 16 }}>
        <h2><FiArchive /> {editMode ? `Edit ${OFFERING_LABEL.singular}` : ACTIONS.new(OFFERING_LABEL)}</h2>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>
          <FiArrowLeft /> Back
        </button>
      </div>

      <div className={styles.formScrollArea}>
      <form id="offerings-form" onSubmit={onSubmit}>
        <div className="card">

          {/* ── Compact two-row header ─────────────────────────────────── */}
          <div className={styles.formHeader}>

            {/* Row 1: Member */}
            <div className={styles.formHeaderRow}>
              {editMode ? (
                <div className={`form-group ${styles.formFieldHalf}`} style={{ margin: 0 }}>
                  <label>Member</label>
                  <input readOnly value={form.userName} className={rStyles.readonlyInput} />
                </div>
              ) : (
                <div className={`form-group ${styles.formFieldHalf}`} style={{ margin: 0 }}>
                  <label>Member *</label>
                  <OfferingsMemberSelect
                    value={form.userId}
                    displayValue={form.userName}
                    displayEmail={form.userEmail || ''}
                    displayPhone={form.userPhone || ''}
                    options={personUsers}
                    disabled={personUsers.length === 0}
                    onSelect={(m) => {
                      setForm((f) => ({ ...f, userId: m.id, userName: m.name, userEmail: m.email || '', userPhone: m.phone || '' }));
                      if (onUserSelect) onUserSelect(m);
                    }}
                  />
                  {personUsers.length === 0 && (
                    <span className={rStyles.hint}>{EMPTY.noMembers}</span>
                  )}
                </div>
              )}
            </div>

            {/* Row 2: Date + Event toggle + Notes */}
            <div className={styles.formHeaderRow}>
              <div className={`form-group ${styles.formFieldDate}`} style={{ margin: 0 }}>
                <label>{OFFERING_LABEL.singular} Date *</label>
                <input
                  type="date"
                  value={form.offeringDate}
                  min={minDate || fyStart || undefined}
                  max={maxDate || fyEnd || undefined}
                  onChange={(e) => setForm((f) => ({ ...f, offeringDate: e.target.value }))}
                  onBlur={(e) => {
                    let val = e.target.value;
                    if (!val) return;
                    const lo = minDate || fyStart;
                    const hi = maxDate || fyEnd;
                    if (lo && val < lo) val = lo;
                    if (hi && val > hi) val = hi;
                    setForm((f) => ({ ...f, offeringDate: val }));
                  }}
                  required
                  disabled={isDateLocked}
                />
              </div>

              <div className={`form-group ${styles.formFieldEvent}`} style={{ margin: 0 }}>
                <label>
                  Event{' '}
                  <em style={{ fontWeight: 400, color: 'var(--color-text-muted)', fontSize: '0.85em' }}>(optional)</em>
                </label>
                <select
                  value={form.eventId || ''}
                  disabled={matchingEvents.length === 0}
                  onChange={(e) => {
                    const val = e.target.value;
                    setForm((f) => ({ ...f, eventId: val || null, isEventDay: !!val }));
                  }}
                >
                  <option value="">
                    {matchingEvents.length === 0 ? 'No event on this date' : '— Select event —'}
                  </option>
                  {matchingEvents.map((ev) => (
                    <option key={ev.id} value={String(ev.id)}>{ev.title}</option>
                  ))}
                </select>
              </div>

              <div className={`form-group ${styles.formFieldGrow}`} style={{ margin: 0 }}>
                <label>
                  {FORM.notes}{' '}
                  <em style={{ fontWeight: 400, color: 'var(--color-text-muted)', fontSize: '0.85em' }}>
                    {FORM.optional}
                  </em>
                </label>
                <input
                  placeholder={FORM.notesPlaceholder}
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </div>
            </div>

          </div>

          <div className={rStyles.divider} />

          {/* ── Section 3: Items ───────────────────────────────────────── */}
          <div className={rStyles.section}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div className={rStyles.sectionLabel}>{OFFERING_LABEL.singular} Items</div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={addItem}
              >
                <FiPlus size={13} /> Add Row
              </button>
            </div>

            <div className={styles.itemsTableWrap} style={{ margin: 0 }}>
              <div className={styles.itemsHead}>
                <span className={styles.colNum}>#</span>
                <span className={styles.colCat}>Donation Category</span>
                <span className={styles.colQty}>Qty</span>
                <span className={styles.colRate}>Default Rate (₹)</span>
                <span className={styles.colAmt}>Amount (₹)</span>
                <span className={styles.colDel} />
              </div>

              <div className={styles.itemsBody}>
                {form.items.map((it, idx) => {
                  const isLockedEditItem = editMode && idx === 0;
                  const shouldLockQtyRate = it.donationCategoryId && (!it.hasDefaultRate || it.manualAmount) && !it.hasDefaultQty && !it.isFixedRate;
                  // When a category has a fixed default rate (or a fixed event-donation rate),
                  // the rate is not user-editable — only Qty can be changed.
                  const shouldLockRate = it.donationCategoryId && (it.hasDefaultRate || it.isFixedRate);
                  return (
                  <div key={it.id} className={styles.itemRow}>
                    <span className={styles.colNum}>
                      <span className={styles.itemIdx}>{idx + 1}</span>
                    </span>

                    <div className={styles.colCat}>
                      {isLockedEditItem ? (
                        <span style={{ padding: '6px 0', display: 'block', fontWeight: 500 }}>
                          {it.categoryName || '—'}
                        </span>
                      ) : (
                        <OfferingsCategorySelect
                          value={it.donationCategoryId}
                          displayValue={it.categoryName}
                          options={categoryOptions}
                          rowIndex={idx}
                          totalRows={form.items.length}
                          onSelect={(c) => {
                            const ed = edByCategory.get(String(c.id));
                            const defaultRate = parseFloat(c.defaultValue);
                            const hasDefaultRate = Number.isFinite(defaultRate) && defaultRate > 0;
                            const defaultQtyVal = parseInt(c.defaultQty, 10);
                            const hasDefaultQty = Number.isFinite(defaultQtyVal) && defaultQtyVal > 0;
                            const qty = hasDefaultQty
                              ? String(defaultQtyVal)
                              : hasDefaultRate ? (parseFloat(it.qty) || '') : '0';
                            const rate = hasDefaultRate ? String(defaultRate) : '0';
                            const amount = hasDefaultRate && qty
                              ? String(Math.round(parseFloat(qty) * defaultRate)) : '';
                            const updates = {
                              donationCategoryId: c.id,
                              categoryName: c.name,
                              qty,
                              rate,
                              amount,
                              hasDefaultRate,
                              hasDefaultQty,
                              manualAmount: false,
                              isFixedRate: false,
                              isSpecialDonation: false,
                              specialRegistered: false,
                            };
                            if (ed?.fixedRate != null) {
                              updates.rate = String(ed.fixedRate);
                              updates.amount = String(Math.round((parseFloat(qty) || 1) * ed.fixedRate));
                              updates.isFixedRate = true;
                            }
                            updateItem(it.id, updates);
                            // focus: qty pre-filled → go to rate; rate pre-filled only → go to qty; else → amount
                            if (hasDefaultQty) {
                              focusItemInput(rateRefs, it.id);
                            } else {
                              focusItemInput(hasDefaultRate || ed?.fixedRate != null ? qtyRefs : amountRefs, it.id);
                            }
                          }}
                        />
                      )}
                    </div>

                    <div className={styles.colQty}>
                      <input
                        type="number" min="1" step="1"
                        ref={(el) => { qtyRefs.current[it.id] = el; }}
                        value={it.qty}
                        onWheel={blockWheel}
                        onChange={(e) => {
                          const qty = e.target.value;
                          const rate = parseFloat(it.rate);
                          const computed = Math.round((parseFloat(qty) || 0) * (rate || 0));
                          updateItem(it.id, { qty, manualAmount: false, ...(rate > 0 ? { amount: String(computed || '') } : {}) });
                        }}
                        disabled={isLockedEditItem || shouldLockQtyRate}
                        className={styles.numInput}
                      />
                    </div>

                    <div className={styles.colRate}>
                      <input
                        type="number" min="0" step="0.01"
                        placeholder="0.00"
                        ref={(el) => { rateRefs.current[it.id] = el; }}
                        value={it.rate}
                        onWheel={blockWheel}
                        onChange={(e) => {
                          const rate = e.target.value;
                          const qty = parseFloat(it.qty) || 1;
                          const computed = Math.round(qty * (parseFloat(rate) || 0));
                          updateItem(it.id, { rate, manualAmount: false, amount: String(computed || '') });
                        }}
                        disabled={isLockedEditItem || shouldLockQtyRate || shouldLockRate}
                        className={styles.numInput}
                      />
                    </div>

                    <div className={styles.colAmt}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input
                          type="number" min="0" step="1"
                          placeholder="0"
                          ref={(el) => { amountRefs.current[it.id] = el; }}
                          value={it.amount}
                          readOnly={!!it.isFixedRate}
                          style={it.isFixedRate ? { background: 'var(--color-bg-secondary)', cursor: 'not-allowed' } : undefined}
                          onWheel={blockWheel}
                          onChange={(e) => {
                            if (it.isFixedRate) return;
                            const val = e.target.value;
                            const rounded = val.includes('.') ? String(Math.round(parseFloat(val))) : val;
                            updateItem(it.id, {
                              amount: rounded,
                              ...(it.hasDefaultRate ? { qty: '0', rate: '0', manualAmount: true } : {}),
                            });
                          }}
                          className={`${styles.numInput} ${itemAmount(it) > 0 ? styles.itemAmtFilled : ''}`}
                        />
                        {it.isFixedRate && <FiLock size={12} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} title="Fixed rate" />}
                      </div>
                    </div>

                    <div className={styles.colDel}>
                      {!isLockedEditItem && (
                        <button
                          type="button"
                          className={styles.removeBtn}
                          onClick={() => removeItem(it.id)}
                          disabled={form.items.length === 1}
                          title="Remove row"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>

              <div className={styles.itemsFooter}>
                <span className={styles.itemsFooterLabel}>Total {OFFERING_LABEL.singular} Amount</span>
                <span className={styles.itemsFooterTotal}>{fmtCurrency(totalAmount)}</span>
              </div>
            </div>

          </div>


        </div>
      </form>
      </div>{/* end formScrollArea */}

      {/* ── Static page footer — never scrolls ─────────────────────── */}
      <div className={styles.pageFooter}>
        <div className={styles.pageFooterLeft}>
          {form.userName && (
            <span className={rStyles.pledgeCount}>{form.userName}</span>
          )}
          {selectedFY && (
            <span className={rStyles.pledgeCount}>
              {selectedFY.yearLabel}
              {form.offeringDate && (
                <> • <FiCalendar size={12} style={{ verticalAlign: 'middle' }} /> {formatDate(form.offeringDate)}</>
              )}
            </span>
          )}
        </div>
        <div className={styles.pageFooterRight}>
          <div className={rStyles.totalBox}>
            <span className={rStyles.totalLabel}>Total {OFFERING_LABEL.singular} Amount</span>
            <span className={rStyles.totalValue}>{fmtCurrency(totalAmount)}</span>
          </div>
          <div className={rStyles.actionBtns}>
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
              {ACTIONS.cancel}
            </button>
            <button type="submit" form="offerings-form" className="btn btn-secondary" disabled={saving}>
              {submitLabel}
            </button>
            {!editMode && onSubmitWithReceipt && canPayAfterCreate && (
              <button
                type="button"
                className="btn btn-primary"
                disabled={saving || validItemCount === 0}
                onClick={onSubmitWithReceipt}
              >
                {isDerasarAdmin ? <FiFileText /> : <FiCreditCard />} {withReceiptLabel}
              </button>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}
